// @flow
import * as React from "react";
import PropTypes from 'prop-types';
import {reisy, use} from "reisy";
import {observer} from "mobx-react";
import {injectIntl} from "react-intl";

import type {User} from "../api/types.js";

import Aside from "../components/aside.js";
import {Header, Background, styles as header} from "../components/header.js";
import Navigation from "../navigation/index.js";
import Footer from "../components/footer.js";
import Menu from "../components/menu.js";
import {Value} from "../components/value.js";
import {isMobile, isIE, hasPositionStickySupport} from "../environment.js";
import SpringSystem from "../pagehandler/math/springsystem.js";
import Flow from "../publishables/flow.js";

export const styles = reisy `
@namespace Kosk;

JackInTheBox: $Settings.JackInTheBox;
YeOldeKosk: $Settings.YeOldeKosk;
Branding: $Settings.Branding;
layout: loose;
HeaderShow: $Header.show;
HeaderContrastHint: #000000;
ContentContrastHint: #ffffff;
anchorSupport: false;

navbarCollapsed: back pagestrip - menu search expand;
navbarExpanded: back pagestrip - collapse | menu | search;
navbarBehavior: transparentontop;

Navigation {

}

Container {
	@extends Helpers.Fullscreen;
	display: flex;
	flex-direction: column;
	min-height: 100vh;
	box-sizing: border-box;
	color: $Colors.TextDark;
}
Contain {
	/*@extends Helpers.Contain;*/
	min-height: auto;
}

offsetNavigation {
	/* use default navigation height, anything else should require manual offset as this can't realistically be set via js */
	padding-top: $Navigation.DefaultHeight;
}

main {
	@extends Helpers.FontSize;
	background: white;
	flex-grow: 1;
	width: 100%;
	max-width: $Settings.KoskMaxWidth;
	margin: 0 auto;
	$Box.box {
		margin: 1.5625em auto;
	}

	&, & *, & *::after, & *::before {
		box-sizing: border-box;
	}
}

isMobile {

}
isDesktop {

}
isIE {
	
}
noPositionStickySupport {

}
isRTL {
	direction: rtl;
}
`;

const ContentNavbar = reisy`
@namespace ContentNavbar;

navbarCollapsed: none;
navbarExpanded: none;
navbarBehavior: static;

Navigation {

}
`

type Props = {
	intl: any; // TODO: properly type this
	kosk: User;
};

class Kosk extends React.Component {
	static contextTypes = {
		player: PropTypes.object,
	};

	props: Props;
	showAside: any;
	orientation: string;
	viewportHeight: any;
	spring: any;
	main: Element;
	lastAnchor: string;

	constructor(props: Props) {
		super(props);
		this.showAside = Value(false);

		this.viewportHeight = Value(IS_BROWSER ? window.innerHeight : 768);
		this.orientation = "";
		this.navigation = React.createRef();

		this.spring = new SpringSystem({
			scroll: val => window.scrollTo(0, val),
		});
	}
	handleScrollToContent = () => {
		const mainTop = this.main.getBoundingClientRect().top;
		const navElem = this.navigation.current && this.navigation.current.elem;
		let navHeight = 0;
		if (navElem) {
			const position = getComputedStyle(navElem).position;
			navHeight = (position === 'fixed' || position === 'sticky') ? navElem.scrollHeight : 0;
		}
		this.spring.reset({scroll: window.scrollY || window.pageYOffset});
		this.spring.set({
			scroll: (window.scrollY || window.pageYOffset) + mainTop - navHeight,
		});
	}
	handleScroll = () => {
		const mainTop = this.main.getBoundingClientRect().top;
		const showAside = mainTop <= 0;
		this.showAside.set(showAside);
		this.lastScroll = (window.scrollY || window.pageYOffset);
	}
	handleOrientation = () => {
		const viewportHeight = window.innerHeight;
		const viewportWidth = window.innerWidth;

		// only reset height details if orientation has changed
		const orientation = viewportHeight > viewportWidth
			? "portrait" : "landscape";
		if (orientation !== this.orientation) {
			this.orientation = orientation;
			this.viewportHeight.set(viewportHeight);
		}
	}
	componentDidMount() {
		if (!IS_BROWSER) { return; }
		window.addEventListener("scroll", this.handleScroll);
		this.handleScroll();
		if (isMobile) {
			window.addEventListener("resize", this.handleOrientation);
			this.handleOrientation();
		}
		this.handleAnchor();
	}
	componentDidUpdate() {
		this.handleAnchor();
	}
	componentWillUnmount() {
		window.removeEventListener("scroll", this.handleScroll);
		if (isMobile) {
			window.removeEventListener("resize", this.handleOrientation);
		}
	}
	handleAnchor() {
		if(!styles.anchorSupport) {
			return;
		}
		const { anchor } = this.context.player.url;
		if (anchor && anchor !== this.lastAnchor) {
			const target = document.getElementById(anchor);
			if (target) {
				this.spring.set({
					scroll: target.offsetTop,
				});
			}
			this.lastAnchor = anchor;
		};
	}
	render() {
		const {kosk, intl, back} = this.props;
		const language = kosk.locales.filter(l => l.code === this.context.player.options.locale)[0];

		// add spacing to offset fixed navigation from covering content
		// if header disabled, nav enabled, nav is fixed and nav not transparent
		const addOffsetNavigation = (
			!styles.HeaderShow &&
			styles.navbarCollapsed !== "none" &&
			styles.navbarBehavior.indexOf("transparent") === -1 &&
			styles.navbarBehavior.indexOf("static") === -1
		);

		const aside = styles.YeOldeKosk
			? <Aside publisher={kosk} show={this.showAside} />
			: null;

		if (!back) {
			styles.navbarCollapsed = styles.navbarCollapsed.replace('back', '');
			styles.navbarExpanded = styles.navbarExpanded.replace('back', '');
		}

		const tags = this.context.player && this.context.player.url && this.context.player.url.query && this.context.player.url.query.tags;

		return (
			<div {...use(styles.Container, styles.JackInTheBox && styles.Contain, isMobile ? styles.isMobile : styles.isDesktop, isIE && styles.isIE, !hasPositionStickySupport && styles.noPositionStickySupport, language && language.bidi && styles.isRTL)} {...(tags ? {["data-tags"]: tags} : {})} data-host={this.context.player.options.absoluteHost}>
				<Navigation styles={styles} contrastHint={header.show ? styles.HeaderContrastHint : styles.ContentContrastHint} ref={this.navigation} />
				{header.show && <Background background={kosk.background} windowHeight={this.viewportHeight} />}
				{header.show && <Header
					publisher={kosk} type={kosk.type}
					onScrollToContent={this.handleScrollToContent}
					windowHeight={this.viewportHeight} />}

				<main {...use(styles.main, addOffsetNavigation && styles.offsetNavigation)} ref={ref => (this.main = ref)}>
					{aside}
					{kosk.menues && <Menu position="main" menues={kosk.menues} />}
					<Navigation styles={ContentNavbar} />
					{Flow(styles.layout, intl.formatMessage, kosk.positions.main)}
				</main>
				<Footer publisher={kosk} />
			</div>
		);
	}
}

export default injectIntl(observer(Kosk));



// WEBPACK FOOTER //
// ./lib/screens/kosk.js